import type { CostType } from "../domain/proposedCostLine";

/**
 * Estimate evidence boundary (Phase 9E, spec §26D).
 *
 * The permanent philosophy:
 *
 *   ESTIMATES MAY BE SMART.
 *   THEIR SOURCE MUST ALWAYS BE EXPLAINABLE.
 *   THE USER MAY ALWAYS OVERRIDE THEM.
 *
 * The estimating engine asks "what should this cost per unit?" and a set of
 * EVIDENCE PROVIDERS answer with real, attributable numbers. Nothing here
 * talks to SQLite, a retailer, or a future Berrier Reno network service:
 * providers implement this interface, so a new source of cost knowledge is
 * added by registering a provider — never by teaching the engine another
 * query.
 *
 * Every CostEvidence carries the observations behind it, so any number the
 * app proposes can be traced back to the purchase, receipt, offer or
 * template it came from. Evidence with nothing behind it does not exist.
 */

export type EvidenceKind =
  /** The user's own decision — always wins. */
  | "manual-override"
  /** What this company actually paid on comparable work. */
  | "organization-history"
  /** A current, real offer for the selected (or candidate) product. */
  | "product-offer"
  /** The assembly template's default assumption. */
  | "template-assumption"
  /** FUTURE: anonymized Berrier Reno network / regional market data. */
  | "network-market";

/** What is being priced. Identity is stable across copy-on-use boundaries. */
export interface EstimateSubject {
  /**
   * `component` prices one line of an assembly (this toilet, this install
   * labor); `feature` prices a whole scope item per unit (all-in $/sqft).
   */
  kind: "component" | "feature";
  /** Library link; null for one-off custom scope (history cannot compare). */
  featureDefinitionId: string | null;
  /** Component identity within the assembly: the copied component name. */
  componentName: string | null;
  /** material / labor / other — evidence NEVER crosses cost bases. */
  costBasis: CostType | null;
  /** The per-unit basis this evidence must be expressed in. */
  unit: string;
  materialDefinitionId: string | null;
  /** The product currently selected for this component, when there is one. */
  productId: string | null;
  /** Human label for the panel heading. */
  label: string;
}

export interface EstimateRequest {
  subject: EstimateSubject;
  /** Market scoping — costs are local. */
  market: { zip: string; label: string };
  /**
   * The project being estimated. Its own records are context, never
   * evidence: an estimate must not learn from itself.
   */
  projectId: string | null;
  /** Quantity being estimated (×100), when known. */
  quantityX100: number | null;
  /** "Now" for recency scoring, ISO date. */
  asOf: string;
}

/** One real data point behind a piece of evidence. */
export interface EvidenceObservation {
  label: string;
  /** Cents ×100 per unit — sub-cent precision before any rounding. */
  unitCostCentsX100: number;
  /** Quantity the observation covers (×100), when known. */
  quantityX100: number | null;
  date: string;
  /** Which job this came from — the user recognises their own work. */
  projectJobNumber: string | null;
  projectId: string | null;
  market: string | null;
  /** Deep link to the authoritative record, so the number is checkable. */
  ref: { kind: "purchase" | "expense" | "price-observation" | "template" | "decision"; id: string } | null;
}

export interface CostEvidence {
  id: string;
  providerId: string;
  providerLabel: string;
  kind: EvidenceKind;
  /** The per-unit cost this evidence supports (cents ×100). */
  unitCostCentsX100: number;
  unit: string;
  /** Spread of the underlying observations (null when a single figure). */
  lowCentsX100: number | null;
  highCentsX100: number | null;
  sampleSize: number;
  /** Most recent observation date (ISO), null when timeless. */
  asOf: string | null;
  market: string | null;
  /** Plain sentences: what this number is and what it is made of. */
  explanation: string[];
  observations: EvidenceObservation[];
  /**
   * How directly this answers the question:
   *  exact       — the same thing, priced now (a current offer, an override)
   *  strong      — genuinely comparable past work
   *  indicative  — a default assumption or a thin/old sample
   */
  quality: "exact" | "strong" | "indicative";
  /**
   * True when the underlying work is not finished, so the figure is not a
   * final cost. Provisional evidence is shown as context and never becomes
   * a recommendation (the Phase 9C rule: incomplete is not a result).
   */
  provisional?: boolean;
}

export interface ProviderAvailability {
  available: boolean;
  /** Why not, in the user's words — shown, never swallowed. */
  note: string | null;
}

export interface EvidenceProvider {
  id: string;
  label: string;
  kind: EvidenceKind;
  availability(): Promise<ProviderAvailability>;
  gather(request: EstimateRequest): Promise<CostEvidence[]>;
}

/**
 * A provider that is DECLARED but not built — the seam for anonymized
 * network data, regional market feeds and third-party cost providers.
 * Declaring it keeps the boundary honest: the UI can say what it would add
 * without anything pretending the data exists.
 */
export function createUnavailableProvider(
  id: string,
  label: string,
  kind: EvidenceKind,
  note: string,
): EvidenceProvider {
  return {
    id,
    label,
    kind,
    async availability() {
      return { available: false, note };
    },
    async gather() {
      return [];
    },
  };
}

/** Evidence never crosses cost bases or units — the 9C discipline. */
export function isComparable(evidence: CostEvidence, subject: EstimateSubject): boolean {
  return evidence.unit === subject.unit;
}
